import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  attachCategoryChildren,
  buildCategoryColumns,
  findCategoryNodeByPath,
  findCategoryPathIds,
  getCategoryRoots,
  isLeafCategoryNode,
} from '@/tiktok-platform/utils/tiktokCategoryTree';

/**
 * @param {object} props
 * @param {Array|object} props.categories
 * @param {(sel: object) => void} [props.onChange]
 * @param {(parentNode: object) => Promise<Array|object>} [props.loadChildren]
 *   非叶子且本地无 children 时按需拉取下级（Tiktok 平台类目树过大，不适合一次同步全量）
 */
export default function CategoryCascader({
  categories,
  onChange,
  compact = false,
  selectedCategoryId = '',
  hideDetail = false,
  onColumnsChange,
  loadChildren,
}) {
  const [tree, setTree] = useState(() => getCategoryRoots(categories));
  const [pathIds, setPathIds] = useState([]);
  const [loadingParentId, setLoadingParentId] = useState('');
  const [loadError, setLoadError] = useState('');
  const syncedSelectedRef = useRef('');
  const treeRef = useRef(tree);
  treeRef.current = tree;

  useEffect(() => {
    setTree(getCategoryRoots(categories));
  }, [categories]);

  const roots = tree;

  useEffect(() => {
    const selectedKey = String(selectedCategoryId || '');
    if (!selectedKey) {
      if (syncedSelectedRef.current) {
        syncedSelectedRef.current = '';
        setPathIds([]);
      }
      return;
    }
    const next = findCategoryPathIds(roots, selectedKey);
    if (!next.length) return;
    if (syncedSelectedRef.current === selectedKey) return;
    syncedSelectedRef.current = selectedKey;
    setPathIds(next);
  }, [roots, selectedCategoryId]);

  const selectedPath = useMemo(
    () => findCategoryNodeByPath(roots, pathIds),
    [roots, pathIds],
  );

  const columns = useMemo(
    () => buildCategoryColumns(roots, pathIds),
    [roots, pathIds],
  );

  useLayoutEffect(() => {
    onColumnsChange?.(columns.length);
  }, [columns.length, onColumnsChange]);

  const selectedLeaf = selectedPath.length > 0
    ? selectedPath[selectedPath.length - 1]
    : null;

  const emitChange = (nextIds, nextRoots) => {
    const nextPath = findCategoryNodeByPath(nextRoots, nextIds);
    const nextLeaf = nextPath.length > 0 ? nextPath[nextPath.length - 1] : null;
    const leafReady = nextLeaf && isLeafCategoryNode(nextLeaf) ? nextLeaf : null;
    onChange?.({
      pathIds: nextIds,
      path: nextPath,
      leaf: leafReady,
      categoryId: leafReady?.category_id ?? null,
      productTypeId: leafReady?.product_type_id || (leafReady ? leafReady.category_id : null),
      categoryPathLabel: nextPath.map((n) => n.category_name).filter(Boolean).join(' / '),
    });
  };

  const handleSelect = async (columnIndex, node) => {
    const nextIds = [...pathIds.slice(0, columnIndex), String(node.category_id)];
    setPathIds(nextIds);
    setLoadError('');

    let nextRoots = treeRef.current;
    const needsLoad = typeof loadChildren === 'function'
      && !isLeafCategoryNode(node)
      && !(Array.isArray(node.children) && node.children.length > 0);

    if (needsLoad) {
      const parentId = String(node.category_id);
      setLoadingParentId(parentId);
      try {
        const childrenRaw = await loadChildren(node);
        nextRoots = attachCategoryChildren(treeRef.current, parentId, childrenRaw);
        setTree(nextRoots);
      } catch (e) {
        setLoadError(e?.message || fixedUi("加载下级类目失败"));
      } finally {
        setLoadingParentId('');
      }
    }

    emitChange(nextIds, nextRoots);
  };

  if (roots.length === 0) {
    return <p className="tiktok-brand-ip__empty">{fixedUi("暂无可发商品分类")}</p>;
  }

  return (
    <div
      className={`tiktok-category-cascader${hideDetail ? ' tiktok-category-cascader--embedded' : ''}`}
      data-testid="tiktok-category-cascader"
    >
      <div className="tiktok-category-cascader__columns">
        {columns.map((column, columnIndex) => (
          <div key={`col-${columnIndex}`} className="tiktok-category-cascader__column">
            {column.map((node) => {
              const id = String(node.category_id);
              const active = pathIds[columnIndex] === id;
              const leaf = isLeafCategoryNode(node);
              const hasChildren = Array.isArray(node.children) && node.children.length > 0;
              const showArrow = !leaf || hasChildren;
              const loading = loadingParentId === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`tiktok-category-cascader__item${active ? ' tiktok-category-cascader__item--active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(columnIndex, node)}
                  disabled={loading}
                >
                  <span className="tiktok-category-cascader__label">
                    {loading
                      ? fixedUi("加载中…")
                      : (node.category_name || node.catName || (id && id !== 'undefined' ? fixedUiTpl('分类 {0}', id) : fixedUi('未命名类目')))}
                  </span>
                  {showArrow ? <span className="tiktok-category-cascader__arrow">›</span> : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {loadError ? <p className="tko-shop-info__inline-error" role="alert">{loadError}</p> : null}

      {hideDetail ? null : selectedLeaf && isLeafCategoryNode(selectedLeaf) ? (
        compact ? (
          <div className="tiktok-category-cascader__detail">
            <div className="tiktok-category-cascader__detail-title">{fixedUi("已选类目")}</div>
            <p className="tiktok-category-cascader__hint" style={{ margin: 0 }}>
              {selectedPath.map((n) => n.category_name).filter(Boolean).join(' / ')}
            </p>
          </div>
        ) : (
          <div className="tiktok-category-cascader__detail">
            <div className="tiktok-category-cascader__detail-title">{fixedUi("已选末级分类")}</div>
            <dl className="tiktok-category-cascader__detail-dl">
              <div>
                <dt>{fixedUi("分类路径")}</dt>
                <dd>{selectedPath.map((n) => n.category_name).filter(Boolean).join(' / ')}</dd>
              </div>
              <div>
                <dt>{fixedUi("分类 ID")}</dt>
                <dd><code>{selectedLeaf.category_id}</code></dd>
              </div>
            </dl>
          </div>
        )
      ) : (
        <p className="tiktok-category-cascader__hint">
          {compact
            ? fixedUi("请逐级选择至末级类目（下级按需加载）。")
            : fixedUi("在左侧逐级选择分类；下级类目点击后按需从平台加载。")}
        </p>
      )}
    </div>
  );
}
